import { homeApi } from '@/features/home/api/homeApi'
import { filterApi } from '@/features/residential-complex/api/filterApi'
import { articlesApi } from '@/features/articles/api/articlesApi'
import { countriesApi } from '@/features/countries/api/countriesApi'
import { localStorageService, StorageKey } from '@/services/storage/localStorage.service'
import type { ResidentialComplex } from '@/types/entities/residential-complex'
import type { FilterValues } from '@/features/residential-complex/api/filterApi'
import type { Article } from '@/types/entities/article'
import type { Country } from '@/types/entities/country'

/**
 * Service для инициализации приложения при старте
 * Загружает критичные данные и кэширует в localStorage
 */
class AppInitializerService {
  private initPromise: Promise<void> | null = null

  /**
   * Инициализация приложения
   * Вызывается в App.vue onMounted
   */
  async initialize(): Promise<void> {
    // Если уже инициализируется, возвращаем существующий промис
    if (this.initPromise) {
      return this.initPromise
    }

    console.log('[AppInitializer] Starting initialization...')

    this.initPromise = this._performInitialization()
    
    try {
      await this.initPromise
      console.log('[AppInitializer] Initialization completed')
    } catch (error) {
      console.error('[AppInitializer] Initialization failed:', error)
    } finally {
      this.initPromise = null
    }
  }

  /**
   * Выполнение инициализации
   */
  private async _performInitialization(): Promise<void> {
    // Запускаем все задачи параллельно
    await Promise.allSettled([
      this.loadFeaturedComplexes(),
      this.loadFeaturedArticles(),
      this.loadFilterValues(),
      this.loadCountries(),
    ])
  }

  /**
   * Загрузить featured комплексы
   */
  private async loadFeaturedComplexes(): Promise<void> {
    try {
      console.log('[AppInitializer] Loading featured complexes...')
      const complexes = await homeApi.getFeaturedComplexes()

      // Сохраняем в localStorage
      localStorageService.set(StorageKey.FEATURED_COMPLEXES, complexes)
      console.log(`[AppInitializer] Saved ${complexes.length} featured complexes to cache`)
    } catch (error) {
      console.error('[AppInitializer] Failed to load featured complexes:', error)
      throw error
    }
  }

  /**
   * Загрузить featured статьи (новости для главной страницы)
   */
  private async loadFeaturedArticles(): Promise<void> {
    try {
      console.log('[AppInitializer] Loading featured news...')
      const articles = await articlesApi.getFeaturedNews()

      // Сохраняем в localStorage
      localStorageService.set(StorageKey.FEATURED_ARTICLES, articles)
      console.log(`[AppInitializer] Saved ${articles.length} featured news to cache`)
    } catch (error) {
      console.error('[AppInitializer] Failed to load featured news:', error)
      throw error
    }
  }

  /**
   * Загрузить значения фильтров
   */
  private async loadFilterValues(): Promise<void> {
    try {
      console.log('[AppInitializer] Loading filter values...')
      const filterValues = await filterApi.getFilterValues()

      // Сохраняем в localStorage
      localStorageService.set(StorageKey.FILTER_VALUES, filterValues)
      
      // Города сохраняем отдельно для быстрого доступа
      if (filterValues.cities) {
        localStorageService.set(StorageKey.CITIES, filterValues.cities)
      }

      console.log('[AppInitializer] Saved filter values to cache:', {
        cities: filterValues.cities?.length || 0,
        projectTypes: filterValues.projectTypes?.length || 0,
      })
    } catch (error) {
      console.error('[AppInitializer] Failed to load filter values:', error)
      throw error
    }
  }

  /**
   * Загрузить справочник стран
   */
  private async loadCountries(): Promise<void> {
    try {
      console.log('[AppInitializer] Loading countries...')
      const countries = await countriesApi.getAll()

      // Сохраняем в localStorage
      localStorageService.set(StorageKey.COUNTRIES, countries)
      console.log(`[AppInitializer] Saved ${countries.length} countries to cache`)
    } catch (error) {
      console.error('[AppInitializer] Failed to load countries:', error)
      throw error
    }
  }

  /**
   * Получить featured комплексы из кэша
   */
  getFeaturedComplexes(): ResidentialComplex[] {
    return localStorageService.get<ResidentialComplex[]>(StorageKey.FEATURED_COMPLEXES) || []
  }

  /**
   * Получить значения фильтров из кэша
   */
  getFilterValues(): FilterValues | null {
    return localStorageService.get<FilterValues>(StorageKey.FILTER_VALUES)
  }

  /**
   * Получить featured статьи из кэша
   */
  getFeaturedArticles(): Article[] {
    return localStorageService.get<Article[]>(StorageKey.FEATURED_ARTICLES) || []
  }

  /**
   * Получить страны из кэша
   */
  getCountries(): Country[] {
    return localStorageService.get<Country[]>(StorageKey.COUNTRIES) || []
  }

  /**
   * Перезагрузить данные, зависящие от локали
   * Вызывается при смене языка
   */
  async reloadLocaleData(): Promise<void> {
    console.log('[AppInitializer] Reloading locale-specific data...')
    
    try {
      await Promise.allSettled([
        this.loadFeaturedComplexes(),
        this.loadFeaturedArticles(),
      ])
      console.log('[AppInitializer] Locale-specific data reloaded')
    } catch (error) {
      console.error('[AppInitializer] Failed to reload locale data:', error)
    }
  }

  /**
   * Очистить кэш
   */
  clearCache(): void {
    localStorageService.remove(StorageKey.FEATURED_COMPLEXES)
    localStorageService.remove(StorageKey.FEATURED_ARTICLES)
    localStorageService.remove(StorageKey.CITIES)
    localStorageService.remove(StorageKey.COUNTRIES)
    localStorageService.remove(StorageKey.FILTER_VALUES)
    console.log('[AppInitializer] Cache cleared')
  }
}

export const appInitializer = new AppInitializerService()
